import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiDollarSign,
  FiFileText,
  FiLoader,
  FiPrinter,
  FiSend,
  FiUser,
  FiX,
} from "react-icons/fi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

export default function SalesInvoiceDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { accessToken } = useAuth();

  const [activeTab, setActiveTab] = useState("overview");
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isIssuing, setIsIssuing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [error, setError] = useState("");

  const loadInvoice = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest(`/sales-invoices/${id}`, {
        accessToken,
      });

      setInvoice(response);
    } catch (requestError) {
      setError(requestError.message || "Could not load invoice.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const totals = useMemo(() => {
    const totalAmount = Number(invoice?.totalAmount || 0);
    const paidAmount = Number(invoice?.paidAmount || 0);
    const creditedAmount = Number(invoice?.creditedAmount || 0);

    return {
      subtotal: Number(invoice?.subtotalAmount || 0),
      discount: Number(invoice?.discountAmount || 0),
      tax: Number(invoice?.taxAmount || 0),
      total: totalAmount,
      paid: paidAmount,
      credited: creditedAmount,
      balance: Math.max(totalAmount - paidAmount - creditedAmount, 0),
    };
  }, [invoice]);

  const paymentDisplayStatus = getPaymentStatus(
    invoice?.paymentStatus,
    invoice?.dueDate,
    totals.balance,
  );

  const taxSummary = useMemo(() => {
    const groups = new Map();

    (invoice?.items || []).forEach((item) => {
      const taxRate = Number(item.taxRate || 0);
      const lineTax = Number(item.lineTaxAmount || 0);
      const lineTotal = Number(item.lineTotalAmount || 0);
      const taxableAmount = Math.max(lineTotal - lineTax, 0);

      const current = groups.get(taxRate) || {
        taxableAmount: 0,
        taxAmount: 0,
      };

      groups.set(taxRate, {
        taxableAmount: current.taxableAmount + taxableAmount,
        taxAmount: current.taxAmount + lineTax,
      });
    });

    return [...groups.entries()].map(([taxRate, values]) => ({
      taxRate,
      ...values,
    }));
  }, [invoice]);

  const issueInvoice = async () => {
    const confirmed = window.confirm(
      `Issue ${invoice.invoiceCode}? Issued invoices cannot be cancelled directly.`,
    );

    if (!confirmed) return;

    setIsIssuing(true);
    setError("");

    try {
      await apiRequest(`/sales-invoices/${invoice.id}/issue`, {
        method: "POST",
        accessToken,
      });

      await loadInvoice();
    } catch (requestError) {
      setError(requestError.message || "Could not issue this invoice.");
    } finally {
      setIsIssuing(false);
    }
  };

  const cancelInvoice = async (reason) => {
    setIsCancelling(true);
    setError("");

    try {
      await apiRequest(`/sales-invoices/${invoice.id}/cancel`, {
        method: "POST",
        accessToken,
        body: { reason },
      });

      setShowCancelModal(false);
      await loadInvoice();
    } catch (requestError) {
      setError(requestError.message || "Could not cancel this invoice.");
    } finally {
      setIsCancelling(false);
    }
  };

  const openPaymentPage = () => {
    navigate("/admindashboard/sales/payment", {
      state: {
        invoiceId: invoice.id,
        invoiceCode: invoice.invoiceCode,
        customerName: invoice.customerNameSnapshot,
        balanceAmount: totals.balance,
        from: location.pathname,
      },
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-[420px] flex items-center justify-center text-primary-700">
        <FiLoader className="animate-spin mr-3" size={24} />
        Loading invoice...
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="bg-white border border-red-100 rounded-3xl p-8">
        <p className="text-red-700">{error || "Invoice not found."}</p>
        <button
          onClick={() => navigate("/admindashboard/sales/invoice/list")}
          className="mt-5 h-11 px-5 rounded-xl bg-primary-700 text-white"
        >
          Back to Invoices
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-5 xl:flex-row xl:justify-between xl:items-start">
        <div>
          <button
            onClick={() => navigate("/admindashboard/sales/invoice/list")}
            className="flex items-center gap-2 text-gray-500 hover:text-primary-700 mb-4"
          >
            <FiArrowLeft />
            Back to Sales Invoices
          </button>

          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-4xl font-bold text-gray-900">
              {invoice.invoiceCode}
            </h1>

            <InvoiceStatusBadge status={toTitle(invoice.status)} />
            <PaymentBadge status={paymentDisplayStatus} />
          </div>

          <p className="text-gray-500 mt-3">
            Sales Invoice Details · {invoice.store?.name || "—"}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => window.print()}
            className="h-11 px-5 rounded-xl border border-gray-200 flex items-center gap-2 hover:bg-gray-50"
          >
            <FiPrinter />
            Print
          </button>

          {invoice.status === "DRAFT" && (
            <>
              <button
                onClick={() => setShowCancelModal(true)}
                className="h-11 px-5 rounded-xl border border-red-200 text-red-600 hover:bg-red-50"
              >
                Cancel Draft
              </button>

              <button
                onClick={issueInvoice}
                disabled={isIssuing}
                className="h-11 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2 disabled:opacity-60"
              >
                {isIssuing ? <FiLoader className="animate-spin" /> : <FiSend />}
                Issue Invoice
              </button>
            </>
          )}

          {invoice.status === "ISSUED" && (
            <button
              onClick={openPaymentPage}
              disabled={totals.balance <= 0}
              className="h-11 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2 disabled:opacity-60"
            >
              <FiDollarSign />
              {totals.balance <= 0 ? "Fully Paid" : "Receive Payment"}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700 flex gap-3">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="grid xl:grid-cols-2 gap-6">
        <InfoPanel
          title="Customer Information"
          subtitle="Snapshot stored when the invoice was created"
          icon={<FiUser size={24} />}
          iconClassName="bg-primary-50 text-primary-700"
          rows={[
            ["Customer", invoice.customerNameSnapshot],
            ["Phone", invoice.customerPhoneSnapshot],
            ["Email", invoice.customerEmailSnapshot],
            ["Billing Address", formatAddress(invoice.billingAddressSnapshot)],
            [
              "Shipping Address",
              formatAddress(invoice.shippingAddressSnapshot),
            ],
          ]}
        />

        <InfoPanel
          title="Invoice Information"
          subtitle="Billing document details"
          icon={<FiFileText size={24} />}
          iconClassName="bg-green-50 text-green-700"
          rows={[
            ["Invoice Number", invoice.invoiceCode],
            ["Invoice Date", formatDate(invoice.invoiceDate)],
            ["Due Date", invoice.dueDate ? formatDate(invoice.dueDate) : "—"],
            ["Sales Order", invoice.salesOrder?.salesOrderNumber],
            ["Store", invoice.store?.name],
            [
              "Sales Executive",
              invoice.createdBy?.employee?.fullName || invoice.createdBy?.email,
            ],
          ]}
        />
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        <MiniCard title="Grand Total" value={formatMoney(totals.total)} />

        <MiniCard
          title="Paid Amount"
          value={formatMoney(totals.paid)}
          valueClassName="text-green-600"
        />

        <MiniCard
          title="Credit Notes"
          value={formatMoney(totals.credited)}
          valueClassName="text-primary-700"
        />

        <MiniCard
          title="Balance Due"
          value={formatMoney(totals.balance)}
          valueClassName="text-red-600"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-8">
        <h2 className="text-2xl font-bold mb-8">Invoice Progress</h2>

        {invoice.status === "CANCELLED" ? (
          <div className="rounded-2xl bg-red-50 border border-red-100 px-5 py-4 text-red-700">
            This invoice draft was cancelled.
            {invoice.cancellationReason
              ? ` Reason: ${invoice.cancellationReason}`
              : ""}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2">
            <ProgressStep title="Draft" completed />

            <ProgressLine completed={invoice.status === "ISSUED"} />

            <ProgressStep
              title="Issued"
              completed={invoice.status === "ISSUED"}
            />

            <ProgressLine
              completed={
                paymentDisplayStatus === "Partial" ||
                paymentDisplayStatus === "Paid"
              }
            />

            <ProgressStep
              title="Partial"
              completed={
                paymentDisplayStatus === "Partial" ||
                paymentDisplayStatus === "Paid"
              }
            />

            <ProgressLine completed={paymentDisplayStatus === "Paid"} />

            <ProgressStep
              title="Paid"
              completed={paymentDisplayStatus === "Paid"}
            />
          </div>
        )}
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="flex overflow-x-auto border-b">
          {["overview", "products", "payments", "timeline"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-8 h-16 capitalize font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === tab
                  ? "border-primary-700 text-primary-700"
                  : "border-transparent text-gray-500"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="p-8">
          {activeTab === "overview" && (
            <div className="grid xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 space-y-6">
                <div className="bg-gray-50 rounded-3xl p-6">
                  <h2 className="text-2xl font-bold mb-6">Invoice Summary</h2>

                  <div className="grid md:grid-cols-2 gap-6">
                    <Summary
                      label="Subtotal"
                      value={formatMoney(totals.subtotal)}
                    />
                    <Summary
                      label="Discount"
                      value={formatMoney(totals.discount)}
                    />
                    <Summary label="Tax" value={formatMoney(totals.tax)} />
                    <Summary
                      label="Grand Total"
                      value={formatMoney(totals.total)}
                    />
                    <Summary label="Paid" value={formatMoney(totals.paid)} />
                    <Summary
                      label="Balance"
                      value={formatMoney(totals.balance)}
                    />
                    <Summary
                      label="Credit Notes"
                      value={formatMoney(totals.credited)}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-3xl p-6">
                  <h2 className="text-2xl font-bold mb-6">Tax Summary</h2>

                  {taxSummary.length ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3">Tax Rate</th>
                            <th className="text-right py-3">Taxable Amount</th>
                            <th className="text-right py-3">Tax Amount</th>
                          </tr>
                        </thead>

                        <tbody>
                          {taxSummary.map((tax) => (
                            <tr
                              key={tax.taxRate}
                              className="border-b last:border-0"
                            >
                              <td className="py-4">GST {tax.taxRate}%</td>
                              <td className="text-right">
                                {formatMoney(tax.taxableAmount)}
                              </td>
                              <td className="text-right font-semibold">
                                {formatMoney(tax.taxAmount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <Empty text="No tax was applied to this invoice." />
                  )}
                </div>

                <div className="bg-gray-50 rounded-3xl p-6">
                  <h2 className="text-2xl font-bold mb-6">Related Documents</h2>

                  <div className="space-y-4">
                    <RelatedDocument
                      title="Sales Order"
                      number={invoice.salesOrder?.salesOrderNumber || "—"}
                      status={toTitle(invoice.salesOrder?.status)}
                    />

                    <RelatedDocument
                      title="Sales Invoice"
                      number={invoice.invoiceCode}
                      status={toTitle(invoice.status)}
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="sticky top-6 space-y-6">
                  <div className="bg-primary-700 rounded-3xl p-6 text-white">
                    <h2 className="text-xl font-bold">Quick Actions</h2>

                    <div className="space-y-3 mt-6">
                      {invoice.status === "ISSUED" && (
                        <button
                          onClick={openPaymentPage}
                          disabled={totals.balance <= 0}
                          className="w-full h-11 rounded-xl bg-white text-primary-700 font-semibold disabled:opacity-60"
                        >
                          {totals.balance <= 0
                            ? "Invoice Fully Paid"
                            : "Receive Payment"}
                        </button>
                      )}

                      {invoice.status === "DRAFT" && (
                        <button
                          onClick={issueInvoice}
                          disabled={isIssuing}
                          className="w-full h-11 rounded-xl bg-white text-primary-700 font-semibold disabled:opacity-60"
                        >
                          {isIssuing ? "Issuing..." : "Issue Invoice"}
                        </button>
                      )}

                      <button
                        onClick={() => window.print()}
                        className="w-full h-11 rounded-xl bg-primary-800"
                      >
                        Print Invoice
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-3xl p-6">
                    <h2 className="text-xl font-bold">Payment Summary</h2>

                    <div className="space-y-4 mt-6">
                      <Summary
                        label="Grand Total"
                        value={formatMoney(totals.total)}
                      />
                      <Summary label="Paid" value={formatMoney(totals.paid)} />
                      <Summary
                        label="Credit Notes"
                        value={formatMoney(totals.credited)}
                      />
                      <Summary
                        label="Balance"
                        value={formatMoney(totals.balance)}
                      />
                      <Summary label="Status" value={paymentDisplayStatus} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "products" && (
            <ProductsTable items={invoice.items || []} />
          )}

          {activeTab === "payments" && (
            <div className="bg-gray-50 rounded-3xl p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">Payment History</h2>

                  <p className="text-gray-500 mt-1">
                    Receipts allocated to this invoice.
                  </p>
                </div>

                {invoice.status === "ISSUED" && totals.balance > 0 && (
                  <button
                    onClick={openPaymentPage}
                    className="h-11 px-5 rounded-xl bg-primary-700 text-white"
                  >
                    Receive Payment
                  </button>
                )}
              </div>

              {invoice.paymentAllocations?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[750px] bg-white rounded-2xl overflow-hidden">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-5 py-4 text-left">Receipt No.</th>
                        <th className="px-5 py-4 text-left">Date</th>
                        <th className="px-5 py-4 text-left">Mode</th>
                        <th className="px-5 py-4 text-left">Reference</th>
                        <th className="px-5 py-4 text-right">Allocated</th>
                        <th className="px-5 py-4 text-center">Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {invoice.paymentAllocations.map((allocation) => {
                        const payment = allocation.payment;

                        return (
                          <tr
                            key={allocation.id}
                            className="border-b border-gray-100 last:border-b-0"
                          >
                            <td className="px-5 py-5 font-semibold">
                              {payment.receiptCode}
                            </td>

                            <td className="px-5 py-5">
                              {formatDate(payment.paymentDate)}
                            </td>

                            <td className="px-5 py-5">
                              {toTitle(payment.paymentMode)}
                            </td>

                            <td className="px-5 py-5">
                              {payment.reference || "—"}
                            </td>

                            <td className="px-5 py-5 text-right font-semibold text-green-600">
                              {formatMoney(allocation.amount)}
                            </td>

                            <td className="px-5 py-5 text-center">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  payment.status === "COMPLETED"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {toTitle(payment.status)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Empty text="No payments have been allocated to this invoice yet." />
              )}
            </div>
          )}

          {activeTab === "timeline" && <Timeline invoice={invoice} />}
        </div>
      </div>

      <CancelInvoiceModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={cancelInvoice}
        isCancelling={isCancelling}
      />
    </div>
  );
}

function ProductsTable({ items }) {
  if (!items.length) {
    return <Empty text="No invoice items found." />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[850px]">
        <thead className="bg-gray-50">
          <tr>
            <TableHeader>Product</TableHeader>
            <TableHeader center>Variant</TableHeader>
            <TableHeader center>Qty</TableHeader>
            <TableHeader right>Rate</TableHeader>
            <TableHeader right>Discount</TableHeader>
            <TableHeader center>GST</TableHeader>
            <TableHeader right>Amount</TableHeader>
          </tr>
        </thead>

        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-b hover:bg-gray-50">
              <td className="px-5 py-5">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-xl bg-gray-100 flex items-center justify-center">
                    <FiFileText size={22} className="text-gray-400" />
                  </div>

                  <div>
                    <h3 className="font-semibold">
                      {item.productNameSnapshot}
                    </h3>
                    <p className="text-sm text-gray-500">{item.skuSnapshot}</p>
                  </div>
                </div>
              </td>

              <td className="px-5 py-5 text-center">
                {item.variantNameSnapshot || "Default"}
              </td>

              <td className="px-5 py-5 text-center">{Number(item.quantity)}</td>

              <td className="px-5 py-5 text-right">
                {formatMoney(item.unitPrice)}
              </td>

              <td className="px-5 py-5 text-right">
                {formatMoney(item.lineDiscountAmount)}
              </td>

              <td className="px-5 py-5 text-center">
                {Number(item.taxRate || 0)}%
              </td>

              <td className="px-5 py-5 text-right font-bold">
                {formatMoney(item.lineTotalAmount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Timeline({ invoice }) {
  const events = [
    {
      title: "Draft created",
      detail: invoice.createdBy?.employee?.fullName || invoice.createdBy?.email,
      date: invoice.createdAt,
      completed: true,
    },
    {
      title: "Invoice issued",
      detail:
        invoice.issuedBy?.employee?.fullName ||
        invoice.issuedBy?.email ||
        "Not issued yet",
      date: invoice.issuedAt,
      completed: Boolean(invoice.issuedAt),
    },
    {
      title: "Invoice cancelled",
      detail: invoice.cancellationReason || "Not cancelled",
      date: invoice.cancelledAt,
      completed: Boolean(invoice.cancelledAt),
    },
  ];

  return (
    <div className="space-y-5">
      {events.map((event) => (
        <div
          key={event.title}
          className="flex gap-4 border-b border-gray-100 pb-5 last:border-0"
        >
          <div
            className={`h-10 w-10 rounded-full shrink-0 flex items-center justify-center ${
              event.completed
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-400"
            }`}
          >
            <FiCheckCircle />
          </div>

          <div>
            <h3 className="font-semibold text-gray-900">{event.title}</h3>
            <p className="text-sm text-gray-500 mt-1">{event.detail}</p>
            <p className="text-xs text-gray-400 mt-2">
              {event.date ? formatDateTime(event.date) : "—"}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function CancelInvoiceModal({ open, onClose, onConfirm, isCancelling }) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="px-7 py-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold">Cancel Invoice Draft</h2>
            <p className="text-sm text-gray-500 mt-1">
              This is allowed only before the invoice is issued.
            </p>
          </div>

          <button
            onClick={onClose}
            className="h-10 w-10 rounded-xl bg-gray-100 flex items-center justify-center"
          >
            <FiX />
          </button>
        </div>

        <div className="p-7">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cancellation reason *
          </label>

          <textarea
            rows="5"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this invoice draft being cancelled?"
            className="w-full rounded-xl border border-gray-200 p-4 resize-none focus:outline-none focus:border-primary-700"
          />
        </div>

        <div className="px-7 py-5 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="h-11 px-5 rounded-xl border border-gray-200"
          >
            Keep Draft
          </button>

          <button
            disabled={!reason.trim() || isCancelling}
            onClick={() => onConfirm(reason.trim())}
            className="h-11 px-5 rounded-xl bg-red-600 text-white disabled:opacity-60"
          >
            {isCancelling ? "Cancelling..." : "Cancel Draft"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoPanel({ title, subtitle, icon, iconClassName, rows }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-8">
      <div className="flex items-center gap-4 mb-8">
        <div
          className={`h-14 w-14 rounded-2xl flex items-center justify-center ${iconClassName}`}
        >
          {icon}
        </div>

        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="text-gray-500">{subtitle}</p>
        </div>
      </div>

      {rows.map(([label, value]) => (
        <Info key={label} label={label} value={value} />
      ))}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex justify-between gap-5 py-4 border-b border-gray-100 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-right max-w-[65%] break-words">
        {value || "—"}
      </span>
    </div>
  );
}

function ProgressStep({ title, completed }) {
  return (
    <div className="flex flex-col items-center gap-2 min-w-[68px]">
      <div
        className={`h-9 w-9 rounded-full flex items-center justify-center ${
          completed ? "bg-green-500 text-white" : "bg-gray-200 text-gray-500"
        }`}
      >
        <FiCheckCircle />
      </div>
      <span
        className={
          completed ? "text-sm font-semibold" : "text-sm text-gray-500"
        }
      >
        {title}
      </span>
    </div>
  );
}

function ProgressLine({ completed }) {
  return (
    <div
      className={`flex-1 min-w-8 h-1 rounded ${completed ? "bg-green-500" : "bg-gray-200"}`}
    />
  );
}

function RelatedDocument({ title, number, status }) {
  return (
    <div className="flex justify-between items-center rounded-2xl bg-white border border-gray-100 px-5 py-4">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="font-semibold mt-1">{number}</p>
      </div>

      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
        {status || "—"}
      </span>
    </div>
  );
}

function MiniCard({ title, value, valueClassName = "" }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6">
      <p className="text-sm text-gray-500">{title}</p>
      <h2 className={`text-3xl font-bold mt-3 ${valueClassName}`}>{value}</h2>
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-right">{value}</span>
    </div>
  );
}

function TableHeader({ children, right = false, center = false }) {
  return (
    <th
      className={`px-5 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 ${
        right ? "text-right" : center ? "text-center" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function InvoiceStatusBadge({ status }) {
  const colors = {
    Draft: "bg-gray-100 text-gray-700",
    Issued: "bg-blue-100 text-blue-700",
    Cancelled: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-4 py-2 rounded-full font-semibold ${colors[status] || colors.Draft}`}
    >
      {status}
    </span>
  );
}

function PaymentBadge({ status }) {
  const colors = {
    Paid: "bg-green-100 text-green-700",
    Partial: "bg-yellow-100 text-yellow-700",
    Unpaid: "bg-red-100 text-red-700",
    Overdue: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${colors[status] || colors.Unpaid}`}
    >
      {status}
    </span>
  );
}

function Empty({ text }) {
  return (
    <div className="py-12 text-center text-gray-500">
      <FiFileText size={28} className="mx-auto text-gray-300 mb-3" />
      {text}
    </div>
  );
}

function getPaymentStatus(paymentStatus, dueDate, balanceAmount) {
  if (paymentStatus === "PAID" || balanceAmount <= 0) return "Paid";
  if (paymentStatus === "PARTIALLY_PAID") return "Partial";

  if (dueDate && new Date(`${dueDate}T23:59:59`) < new Date()) {
    return "Overdue";
  }

  return "Unpaid";
}

function toTitle(value = "") {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "—";

  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAddress(value) {
  if (!value) return "—";

  if (typeof value === "string") return value;

  if (typeof value === "object") {
    return [
      value.addressLine1,
      value.addressLine2,
      value.city,
      value.state,
      value.postalCode,
      value.country,
    ]
      .filter(Boolean)
      .join(", ");
  }

  return "—";
}
